(ns app.front.view.business.subs
  (:require [app.domain.business.model :as business-model]
            [app.domain.group.model :as group-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.domain.licence.model :as licence-model]
            [app.domain.util :as du]
            [app.front.dashlets :as dashlets]
            [app.front.entities :as entities]
            [app.front.view.licence.util :as licence-util]
            [app.front.view.party.business :as party-business]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def ^:private page-state-path (interceptors/path ::state))


(rf/reg-event-db ::toggle-invalid
  [page-state-path]
  (fn [page-data [_ view-id]]
    (update-in page-data [:show-invalid? view-id] not)))


(rf/reg-event-db ::set-licences-view-type
  [page-state-path]
  (fn [page-data [_ id view-type]]
    (assoc-in page-data [:licences-view-type id] view-type)))


(defn- merge-entities-response [_ [_ status {:keys [entities]}]]
  {:fx [[:dispatch [::entities/merge-entity-db status entities]]]})


(rf/reg-event-fx ::fetch-business
  (fn [_ [_ business-id dashlet-id]]
    {:comm/command {:command    :business/get
                    :method     :get
                    :data       (util/mmap business-id)
                    :on-success [::fetch-business.success dashlet-id]}}))

(rf/reg-event-fx ::fetch-business.success
  (fn [{:keys [db]} [_ dashlet-id status {:keys [visibility entities tasks]}]]
    {:fx [[:dispatch [::entities/merge-entity-db status entities]]
          [:dispatch [::dashlets/assoc-dashlet-data dashlet-id status tasks]]]
     :db (-> db
             (assoc-in [::state :visibility] visibility))}))


(rf/reg-event-fx ::fetch-licences
  [page-state-path]
  (fn [{page-data :db} [_ business-id]]
    {:comm/command {:command    :licence/business-licences
                    :method     :get
                    :data       (util/mmap business-id)
                    :on-success [::fetch-licences.success]}
     :db           (assoc page-data :licences-loading? true)}))

(rf/reg-event-fx ::fetch-workspaces
  [page-state-path]
  (fn [{page-data :db} [_ business-id]]
    {:comm/command {:command    :business/get-workspaces
                    :method     :get
                    :data       (util/mmap business-id)
                    :on-success [::fetch-workspaces.success]}
     :db           (assoc page-data :workspaces-loading? true)}))

(defn fetch-handler-fn [loading-key]
  (fn [{page-data :db :as cofx} ev]
    (assoc (merge-entities-response cofx ev)
           :db (assoc page-data loading-key false))))

(rf/reg-event-fx ::fetch-licences.success
  [page-state-path]
  (fetch-handler-fn :licences-loading?))

(rf/reg-event-fx ::fetch-workspaces.success
  [page-state-path]
  (fetch-handler-fn :workspaces-loading?))

(rf/reg-event-fx ::invite-user-to-business
  (fn [{:keys [db]} [_ business-id user-email group-ids]]
    {:comm/command {:command    :business/invite-user
                    :method     :post
                    :data       (util/mmap business-id user-email group-ids)
                    :on-success [::invite-user-to-business.success user-email]}
     :db           (update-in db [::state :invites-pending] #(conj (or % #{}) user-email))}))

(rf/reg-event-fx ::invite-user-to-business.success
  (fn [{:keys [db]} [_ user-email status resp]]
    {:fx [[:dispatch [::entities/merge-entity-db status resp]]]
     :db (update-in db [::state :invites-pending] disj user-email)}))

(defn set-member-op-pending [db user-or-invite-id]
  (update-in db [::state :member-operations-pending] #(conj (or % #{}) user-or-invite-id)))

(defn unset-member-op-pending [db user-or-invite-id]
  (update-in db [::state :member-operations-pending] disj user-or-invite-id))

(rf/reg-event-fx ::update-user-groups
  (fn [{:keys [db]} [_ business-id user-id group-ids]]
    {:comm/command {:command    :business/update-user-groups
                    :method     :post
                    :data       (util/mmap business-id user-id group-ids)
                    :on-success [::user-or-invite-op.success user-id]}
     :db           (set-member-op-pending db user-id)}))

(defn handle-remove-fn [command]
  (fn [{:keys [db]} [_ {:keys [business-id id]}]]
    {:db           (set-member-op-pending db id)
     :comm/command {:command    command
                    :method     :post
                    :data       (util/mmap business-id id)
                    :on-success [::user-or-invite-op.success id]}}))

(rf/reg-event-fx ::remove-user
  (handle-remove-fn :business/remove-user))

(rf/reg-event-fx ::withdraw-invite
  (handle-remove-fn :business/withdraw-invite))

(rf/reg-event-fx ::user-or-invite-op.success
  (fn [{:keys [db]} [_ id status resp]]
    {:dispatch [::entities/apply-changes status resp]
     :db       (unset-member-op-pending db id)}))

(rf/reg-event-db ::edit-info
  [page-state-path]
  (fn [state [_ business]]
    (-> state
        (assoc :info-editing? true)
        (assoc :info-edit-form
               (-> business
                   (update :business/names i18n-model/strings-map)
                   (p/update-if-contains :business/trade-names i18n-model/strings-map))))))

(rf/reg-event-db ::cancel-info-editing
  [page-state-path]
  (fn [state _]
    (-> state
        (dissoc :info-editing?)
        (dissoc :info-edit-form))))

(rf/reg-event-db ::update-info-value
  [page-state-path]
  (fn [state [_ field value]]
    (if (nil? value)
      (p/dissoc-in state [:info-edit-form field])
      (assoc-in state [:info-edit-form field] value))))

(rf/reg-event-db ::dissoc-info-value
  [page-state-path]
  (fn [state [_ field]]
    (update state :info-edit-form dissoc field)))

(rf/reg-event-fx ::update-business
  (fn [_ [_ business modified-business]]
    {:comm/command {:command    :business/update
                    :data       (util/mmap {:business-id (:db/id business)} modified-business)
                    :on-success [::update-business.success]}}))

(rf/reg-event-fx ::update-business.success
  (fn [{:keys [db]} [_ status resp]]
    {:db       (-> db
                   (update ::state dissoc :info-editing?)
                   (update ::state dissoc :info-edit-form))
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::set-email-notifications
  (fn [_ [_ business-id enabled?]]
    {:comm/command {:command    :business/set-email-notifications
                    :data       (util/mmap business-id enabled?)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-db ::show-edit-groups-modal
  [page-state-path]
  (fn [state [_ business-id user-id group-ids]]
    (assoc state :edit-groups-modal (util/mmap business-id user-id group-ids))))

(rf/reg-event-db ::cancel-edit-groups
  [page-state-path]
  (fn [state _]
    (dissoc state :edit-groups-modal)))

(rf/reg-event-db ::set-groups-in-edit
  [page-state-path]
  (fn [state [_ group-ids]]
    (-> state
        (assoc-in [:edit-groups-modal :group-ids] group-ids))))

;; subscriptions

(rf/reg-sub ::state
  :-> ::state)

(rf/reg-sub ::visibility
  :<- [::state]
  :-> :visibility)

(rf/reg-sub ::edit-groups-modal-data
  :<- [::state]
  :-> :edit-groups-modal)

(rf/reg-sub ::edit-groups-modal-open?
  :<- [::edit-groups-modal-data]
  :-> some?)


(rf/reg-sub ::show-invalid?
  :<- [::state]
  (fn [state [_ id]]
    (get-in state [:show-invalid? id] false)))


(rf/reg-sub ::licences-view-type
  :<- [::state]
  (fn [state [_ id]]
    (get-in state [:licences-view-type id] :view/cards)))


(rf/reg-sub ::licences-loading?
  :<- [::state]
  (fn [state _]
    (:licences-loading? state true)))

(rf/reg-sub ::workspaces-loading?
  :<- [::state]
  (fn [state _]
    (:workspaces-loading? state true)))


(rf/reg-sub ::invites-pending
  :<- [::state]
  :-> :invites-pending)

(rf/reg-sub ::operations-pending
  :<- [::state]
  :-> :member-operations-pending)

(rf/reg-sub ::invites-pending-emails
  :<- [::invites-pending]
  (fn [email-set _]
    (some->> email-set
             sort
             seq
             (str/join ", "))))

(rf/reg-sub ::user-is-admin?
  (fn [[_ business-id]]
    [(rf/subscribe [::entities/entity business-id])
     (rf/subscribe [:session/admin?])])
  (fn [[business admin?] _]
    (or (-> business :granted-permissions (contains? :permission.business/write))
        admin?)))

(rf/reg-sub ::name
  (fn [[_ business-id]]
    [(rf/subscribe [::entities/entity business-id])
     (rf/subscribe [:language])])
  (fn [[business lang] _]
    (business-model/format-name business lang)))

(defn reverse-sort-by-issue-date [licence-like]
  (sort-by gl-model/issue-date (comp - compare) licence-like))

(rf/reg-sub ::business-licences
  :<- [::entities/context]
  (fn [ctx [_ business-id]]
    (->> (entities/find-entities ctx {:entity/type       :entity.type/licence
                                      :licence/issued-to business-id})
         (filter (apply du/in-state-fn licence-model/visible-licence-states)))))

(rf/reg-sub ::analyzed-business-licences
  (fn [[_ business-id] _] (rf/subscribe [::business-licences business-id]))
  (fn [licences _]
    (->> licences
         (group-by #(if (licence-util/valid? %) :valid :invalid))
         (p/map-vals reverse-sort-by-issue-date))))

(rf/reg-sub ::analyzed-business-registrations
  (fn [[_ business-id] _]
    (rf/subscribe [::entities/entities
                   {:entity/type                        :entity.type/contractor-registration
                    :contractor-registration/registrant business-id}]))
  (fn [crs _]
    (->> crs
         (group-by #(if (du/in-state? :contractor-registration.state/active %) :valid :invalid))
         (p/map-vals reverse-sort-by-issue-date))))

(rf/reg-sub ::workspaces
  (fn [[_ business-id]]
    (rf/subscribe [::entities/entities {:party/business business-id}]))
  (fn [parties _]
    (->> parties
         (map (comp :entity/parent :entity/parent))
         distinct
         (filter business-model/visible-workspace?)
         (sort-by :workspace/id)
         (group-by :workspace/permit-type))))

(rf/reg-sub ::users
  (fn [[_ business-id]]
    (rf/subscribe [::entities/entity business-id]))
  (fn [business _]
    (->> business
         :business/domain-group
         eu/descendants
         (mapcat :user/_groups)
         distinct
         (remove nil?))))

(defn business-and-user-signal [[_ business-id]]
  [(rf/subscribe [:session/user-id])
   (rf/subscribe [::entities/entity business-id])])

(defn user-in-group-fn [f]
  (fn [[user-id business] _]
    (-> (->> business
             :business/domain-group
             group-model/child-groups
             (filter f)
             (mapcat :user/_groups)
             du/entity-id-set)
        (contains? user-id))))

(rf/reg-sub ::notifications-enabled?
  business-and-user-signal
  (user-in-group-fn (partial group-model/has-role? :group.role/email-recipient)))

(rf/reg-sub ::is-member?
  business-and-user-signal
  (user-in-group-fn (constantly true)))

(rf/reg-sub ::invites
  (fn [[_ business-id]]
    (rf/subscribe [::entities/entities {:task/reference business-id
                                        :task/type      :task.type/join-business}]))
  :-> identity)

(defn groups->names [groups]
  (->> groups
       (keep :group/name)
       sort
       (str/join ", ")))

(rf/reg-sub ::users-and-invites
  (fn [[_ business-id]]
    [(rf/subscribe [::users business-id])
     (rf/subscribe [::invites business-id])
     (rf/subscribe [::user-groups business-id])
     (rf/subscribe [::operations-pending])])
  (fn [[users invites user-groups pending-ops] _]
    (let [user-group-ids (-> user-groups keys set)]
      (->> (concat
             (->> users
                  (map (fn [{:db/keys [id] :as user}]
                         (let [user-groups (->> user
                                                :user/groups
                                                (filter (comp user-group-ids :db/id)))]
                           (merge (select-keys user [:db/id :entity/type])
                                  {:business.users/loading?  (contains? pending-ops id)
                                   :business.users/status    :business.users.status/active
                                   :business.users/name      (user-model/full-name user)
                                   :business.users/email     (:user/email user)
                                   :business.users/group-ids (du/entity-id-set user-groups)
                                   :business.users/groups    (groups->names user-groups)})))))
             (->> invites
                  (map (fn [{:db/keys [id] :as task}]
                         (merge (select-keys task [:db/id :entity/type])
                                {:business.users/loading?  (contains? pending-ops id)
                                 :business.users/status    (case (:domain/state task)
                                                             :task.state/open :business.users.status/invited
                                                             :business.users.status/declined)
                                 :business.users/email     (:task/email task)
                                 :business.users/group-ids (-> task :task.join-business/groups du/entity-id-set)
                                 :business.users/groups    (-> task :task.join-business/groups groups->names)})))))
           (sort-by (juxt :business.users/name :business.users/email))))))

(rf/reg-sub ::user-groups
  (fn [[_ business-id]]
    (rf/subscribe [::entities/entity business-id]))
  (fn [business _]
    (->> business
         :business/domain-group
         eu/descendants
         (filter #(-> % :group/type (= :group.type/user-group)))
         (sort-by :group/name)
         (mapcat (juxt :db/id identity))
         (apply array-map))))

(rf/reg-sub ::show-users-section?
  (fn [[_ business-id]]
    [(rf/subscribe [::user-is-admin? business-id])
     (rf/subscribe [::users-and-invites business-id])])
  (fn [[admin? content] _]
    (boolean (or admin? (seq content)))))

(rf/reg-sub ::info-edit-form
  :<- [::state]
  :-> :info-edit-form)

(rf/reg-sub ::info-editing?
  :<- [::state]
  :-> :info-editing?)

(rf/reg-sub ::info-edit-form-field
  :<- [::info-edit-form]
  (fn [form [_ field]]
    (-> form field)))

(rf/reg-sub ::personal-id-validator-fn
  :<- [::info-edit-form]
  business-model/personal-id-validator)

(rf/reg-sub ::personal-id-schema
  :<- [::personal-id-validator-fn]
  (fn [valid? _]
    (some-> valid?
            (s/pred 'valid-personal-id))))

(rf/reg-sub ::schema
  :<- [::personal-id-schema]
  (fn [personal-id-schema _]
    (if personal-id-schema
      (st/assoc business-model/BusinessEditInfo
                (s/optional-key :business/personal-id)
                personal-id-schema)
      business-model/BusinessEditInfo)))

(rf/reg-sub ::validator-fn
  :<- [::schema]
  :<- [::info-edit-form]
  :<- [::personal-id-validator-fn]
  party-business/validator-fn)

(rf/reg-sub ::form-valid?
  :<- [::schema]
  :<- [::info-edit-form]
  (fn [[schema form] _]
    (nil? (s/check schema form))))
